import { Box, Flex, rem, rgba, Text, useMantineTheme } from "@mantine/core";
import type { KeyboardEvent, ReactNode } from "react";
import { fontWeights } from "@/design-system/tokens";
import classes from "./Tab.module.css";

interface TabProps {
  text: string;
  /** Ícone à esquerda do rótulo; herda a cor do estado (ativo/inativo). */
  icon?: ReactNode;
  isActive?: boolean;
  onClick?: () => void;
  isAccessible?: boolean;
  titleTooltip?: string;
  /** Contador ao lado do rótulo (ex.: quantidade configurada na aba). Omitido quando 0/undefined. */
  count?: number;
  /** Tabindex do roving focus (WAI-ARIA tabs): só uma aba entra no Tab. Padrão: a ativa. */
  tabIndex?: number;
  /** Nome acessível quando o rótulo sozinho não diz tudo (ex.: status ao lado). */
  ariaLabel?: string;
}

const Tab = ({
  text,
  icon,
  isActive = false,
  onClick = () => {},
  isAccessible = true,
  titleTooltip,
  count,
  tabIndex,
  ariaLabel,
}: TabProps) => {
  const theme = useMantineTheme();
  const showCount = typeof count === "number" && count > 0;
  const color = isActive
    ? theme.colors["plain_text"][0]
    : rgba(theme.colors["plain_text"][0], 0.5);

  // Enter/Espaço ativam a aba focada; as setas ficam com o TabsList.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <Flex
      role="tab"
      aria-selected={isActive}
      aria-label={ariaLabel}
      tabIndex={tabIndex ?? (isActive ? 0 : -1)}
      className={classes.tab}
      onKeyDown={handleKeyDown}
      title={titleTooltip ?? text}
      direction={"row"}
      gap={rem(6)}
      style={{
        // O sublinhado da aba ativa agora é a barra deslizante única do TabsList.
        cursor: isAccessible ? "pointer" : "auto",
      }}
      onClick={onClick}>
      {icon && (
        <Box
          aria-hidden
          c={color}
          mb={rem(10)}
          style={{ display: "flex", alignSelf: "center" }}>
          {icon}
        </Box>
      )}
      <Text
        ta={"center"}
        p={"0px 0px 10px 0px"}
        fz="md"
        fw={isActive ? fontWeights.bold : fontWeights.regular}
        style={{ color }}>
        {text}
      </Text>
      {showCount && (
        <Text
          component="span"
          fz="xs"
          fw={fontWeights.bold}
          px={rem(6)}
          mb={rem(10)}
          style={{
            alignSelf: "center",
            lineHeight: rem(18),
            borderRadius: rem(999),
            background: isActive
              ? rgba(theme.colors["R500"][0], 0.14)
              : rgba(theme.colors["plain_text"][0], 0.08),
            color: isActive
              ? theme.colors["R500"][0]
              : rgba(theme.colors["plain_text"][0], 0.6),
          }}
          aria-label={`${count} ${count === 1 ? "item" : "itens"}`}>
          {count}
        </Text>
      )}
    </Flex>
  );
};

export default Tab;
